<script setup>
import { computed, ref } from "vue";

/**
 * Focal point picker for a cover photo (BH-38).
 *
 * Shows the whole image with a draggable marker; tapping or dragging sets
 * the point as percentages of the image, and the arrow keys nudge it for
 * keyboard users. A strip beneath previews the cover crop with that point
 * applied (bg-cover + background-position), so the effect is visible
 * before saving.
 */
const props = defineProps({
    // { x, y } as 0–100 percentages of the image.
    modelValue: {
        type: Object,
        default: () => ({ x: 50, y: 50 }),
    },
    src: {
        type: String,
        required: true,
    },
    alt: {
        type: String,
        default: "",
    },
    // CSS aspect-ratio for the preview strip (e.g. "1472 / 264" for the
    // profile hero). Defaults to the list cover's fixed 90px strip.
    previewAspect: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(["update:modelValue"]);

const image = ref(null);
const dragging = ref(false);

const clamp = (value) => Math.min(100, Math.max(0, Math.round(value)));

function setFromPointer(event) {
    const box = image.value?.getBoundingClientRect();

    if (!box?.width || !box?.height) {
        return;
    }

    emit("update:modelValue", {
        x: clamp(((event.clientX - box.left) / box.width) * 100),
        y: clamp(((event.clientY - box.top) / box.height) * 100),
    });
}

function onPointerDown(event) {
    dragging.value = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setFromPointer(event);
}

function onPointerMove(event) {
    if (dragging.value) {
        setFromPointer(event);
    }
}

function onPointerUp() {
    dragging.value = false;
}

const nudges = {
    ArrowLeft: { x: -1, y: 0 },
    ArrowRight: { x: 1, y: 0 },
    ArrowUp: { x: 0, y: -1 },
    ArrowDown: { x: 0, y: 1 },
};

function onKeydown(event) {
    const nudge = nudges[event.key];

    if (!nudge) {
        return;
    }

    event.preventDefault();
    const step = event.shiftKey ? 10 : 1;
    emit("update:modelValue", {
        x: clamp(props.modelValue.x + nudge.x * step),
        y: clamp(props.modelValue.y + nudge.y * step),
    });
}

const position = computed(
    () => `${props.modelValue.x}% ${props.modelValue.y}%`,
);
</script>

<template>
    <div>
        <div
            class="relative mx-auto w-fit max-w-full cursor-crosshair touch-none select-none"
            data-test="focus-stage"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
        >
            <img
                ref="image"
                :src="src"
                :alt="alt"
                draggable="false"
                class="block max-h-[240px] max-w-full rounded-[5px]"
            />
            <button
                type="button"
                class="absolute size-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-solid border-white bg-brand-red/80 shadow-[0_2px_8px_rgba(0,0,0,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
                :style="{ left: `${modelValue.x}%`, top: `${modelValue.y}%` }"
                aria-label="Focus point"
                data-test="focus-marker"
                @keydown="onKeydown"
            ></button>
        </div>

        <!-- The cover as it will crop with this focus point -->
        <div
            class="mt-3 w-full rounded-[5px] bg-brand-navy bg-cover"
            :class="previewAspect ? '' : 'h-[90px]'"
            :style="{
                backgroundImage: `url(${src})`,
                backgroundPosition: position,
                aspectRatio: previewAspect ?? undefined,
            }"
            data-test="focus-preview"
            aria-hidden="true"
        ></div>
    </div>
</template>
